// File: packages/core/src/hooks/session/useSseManager.js
import { useEffect } from 'react';
import SSEClientWithAuth from '../../services/SSEClientWithAuth';
import { isTokenExpired } from '../../utils/tokenStorage';
import { refreshToken } from '../../services/authService';

export const useSseManager = ({
    sessionId,
    user,
    isAuthenticated,
    isReconnecting,
    reconnectPendingOnShutdown,
    reconnectTrigger,
    hasAuthFailed,
    sseClient,
    isMounted,
    processingStatusRef,
    isReconnectingRef,
    reconnectPendingOnShutdownRef,
    addTimelineItem,
    setIsConnected,
    setProcessingStatus,
    setIsSending,
    setHasAuthFailed,
    setReconnectPendingOnShutdown,
    setIsReconnecting,
    setReconnectAttempts,
    resetSessionInitialization,
    setIsSubscriptionRequired,
    addMessage,
    removeMessage,
    onResourceUpdateNeededRef,
    onResourceSyncRef,
    onAppEventRef,
    sessionToReconnectRef,
    messageIdTrackersRef,
    lastUserActivity, // New prop
    setIsIdleDisconnected, // New prop
}) => {
    useEffect(() => {
        if (!sessionId || !isAuthenticated) {
            if (sseClient.current) {
                console.log("[SSE useEffect] Disconnecting due to no session ID or auth.");
                sseClient.current.disconnect();
                sseClient.current = null;
            }
            return;
        }

        console.log(`[SSE useEffect] Triggered. Session ID: '${sessionId}', Reconnect Trigger: ${reconnectTrigger}. Setting up new SSE client.`);

        if (sseClient.current) {
            sseClient.current.disconnect();
        }

        const sseStateHandlers = {
            getLastId: () => messageIdTrackersRef.current[sessionId] || 0,
            setLastId: (id) => {
                if (!messageIdTrackersRef.current) {
                    messageIdTrackersRef.current = {};
                }
                messageIdTrackersRef.current[sessionId] = Math.max(messageIdTrackersRef.current[sessionId] || 0, id);
            },
        };

        const newSseClient = new SSEClientWithAuth(null, sseStateHandlers);
        sseClient.current = newSseClient;

        const setupSse = async () => {
            try {
                if (isTokenExpired()) {
                    console.log("[useSseManager] Token expired (likely due to device sleep). Refreshing before SSE connect...");
                    try {
                        await refreshToken();
                    } catch (refreshErr) {
                        console.error("[useSseManager] Failed to refresh token before connect:", refreshErr);
                    }
                }

                await newSseClient.connect(sessionId, user);

                if (!isMounted.current) return;

                setIsConnected(true);

                const messageUnsubscribe = newSseClient.onMessage((message) => {
                    if (!isMounted.current) return;
                    addTimelineItem({ ...message, type: 'message' });
                    if (message.source !== 'User') {
                        setProcessingStatus('success');
                    }
                });

                const errorUnsubscribe = newSseClient.onError((errorEvent) => {
                    if (!isMounted.current) return;
                    console.log("[useSessionWithAuth] onError handler received:", errorEvent);
                    if (errorEvent.content?.toLowerCase().includes('active subscription')) {
                        console.warn("[useSessionWithAuth] Subscription required error detected from backend via ERROR msg_type.");
                        setIsSubscriptionRequired(true);
                    }
                    addTimelineItem({
                        ...errorEvent,
                        type: 'message',
                        source: 'System',
                    });
                    setProcessingStatus('error');
                });

                const thinkingUnsubscribe = newSseClient.onThinking((thinking) => {
                    if (!isMounted.current) return;
                    addTimelineItem({ ...thinking, type: 'thinking' });
                });

                const commandUnsubscribe = newSseClient.onCommand((command) => {
                    if (!isMounted.current) return;
                    const commandType = command.type || 'internal_command';
                    addTimelineItem({ ...command, type: commandType });
                    if (command.type === 'resource_update_signal' || command.msg_type === 'RESOURCE') {
                        if (typeof onResourceUpdateNeededRef.current === 'function') {
                            onResourceUpdateNeededRef.current(command.resourceInfo || command);
                        }
                    }
                });

                const resourceUnsubscribe = newSseClient.onResource((resource) => {
                    if (!isMounted.current) return;
                    addTimelineItem({ ...resource, type: 'resource' });
                    if (typeof onResourceUpdateNeededRef.current === 'function') {
                        onResourceUpdateNeededRef.current(resource);
                    }
                });

                const resourceSyncUnsubscribe = newSseClient.onResourceSync((syncEvent) => {
                    if (!isMounted.current) return;
                    if (typeof onResourceSyncRef.current === 'function') {
                        onResourceSyncRef.current(syncEvent);
                    }
                    addTimelineItem({
                        ...syncEvent,
                        type: 'resource_sync',
                        timestamp: syncEvent.timestamp || new Date().toISOString(),
                    });
                });

                const appEventUnsubscribe = newSseClient.onAppEvent((event) => {
                    if (!isMounted.current) return;
                    if (typeof onAppEventRef.current === 'function') {
                        onAppEventRef.current(event);
                    }
                });

                const shutdownUnsubscribe = newSseClient.onShutdownSignal((signal) => {
                    if (!isMounted.current || isReconnectingRef.current) return;
                    console.warn("[useSessionWithAuth] Received INSTANCE_SHUTDOWN_IMMINENT signal.", signal);
                    sessionToReconnectRef.current = sessionId;
                    newSseClient.disconnect();
                    if (processingStatusRef.current === 'processing') {
                        console.log("[useSessionWithAuth] System is busy. Queuing reconnect.");
                        setReconnectPendingOnShutdown(true);
                    } else {
                        console.log("[useSessionWithAuth] System is idle. Starting reconnection loop.");
                        setIsReconnecting(true);
                        setReconnectAttempts(0);
                    }
                });

                const connectionUnsubscribe = newSseClient.onConnectionChange(({ connected, error }) => {
                    if (!isMounted.current) return;
                    setIsConnected(connected);
                    if (!connected) {
                        setProcessingStatus('idle');
                        setIsSending(false);
                        if (error?.status === 401 || error?.message?.includes('401')) {
                            addMessage({ id: 'auth-error', text: "SSE connection failed: Unauthorized. Please log in again.", severity: 'error' });
                            setHasAuthFailed(true);
                            resetSessionInitialization();
                        } else if (error) {
                            console.warn(`SSE connection lost. Error: ${error?.message || 'Connection lost'}`);
                            if (!isReconnectingRef.current && !reconnectPendingOnShutdownRef.current) {
                                // IDLE CHECK LOGIC
                                const IDLE_TIMEOUT = 10 * 60 * 1000; // 10 minutes
                                const timeSinceActivity = Date.now() - (lastUserActivity?.current || 0);

                                if (timeSinceActivity > IDLE_TIMEOUT) {
                                    console.log(`[useSseManager] User inactive for ${(timeSinceActivity/60000).toFixed(1)}m. Preventing auto-reconnect.`);
                                    setIsIdleDisconnected(true);
                                    addMessage({
                                        id: 'idle-disconnect',
                                        text: "Session disconnected due to inactivity.",
                                        severity: 'info',
                                        action: {
                                            label: "Reconnect",
                                            onClick: () => {
                                                removeMessage('idle-disconnect');
                                                setIsIdleDisconnected(false);
                                                sessionToReconnectRef.current = sessionId;
                                                setIsReconnecting(true);
                                                setReconnectAttempts(0);
                                                // Update activity so we don't immediately disconnect again if connection is flaky
                                                if (lastUserActivity) lastUserActivity.current = Date.now();
                                            }
                                        }
                                    });
                                } else {
                                    console.log("[useSessionWithAuth] Unplanned SSE disconnection. Starting reconnection loop.");
                                    sessionToReconnectRef.current = sessionId;
                                    setIsReconnecting(true);
                                    setReconnectAttempts(0);
                                }
                            }
                        }
                    } else {
                        if (!hasAuthFailed) removeMessage('auth-error');
                    }
                });

                return () => {
                    console.log(`[SSE useEffect Cleanup] Cleaning up listeners and disconnecting for session: ${sessionId}`);
                    messageUnsubscribe();
                    errorUnsubscribe();
                    thinkingUnsubscribe();
                    commandUnsubscribe();
                    resourceUnsubscribe();
                    resourceSyncUnsubscribe();
                    appEventUnsubscribe();
                    connectionUnsubscribe();
                    shutdownUnsubscribe();
                    newSseClient.disconnect();
                    if (sseClient.current === newSseClient) {
                        sseClient.current = null;
                    }
                };
            } catch (error) {
                console.error(`[SSE useEffect] Failed to connect SSE client for session ${sessionId}:`, error);
                if (isMounted.current && !isReconnectingRef.current) {
                    console.log("[useSseManager] Initial connection failed, triggering reconnection loop.");
                    sessionToReconnectRef.current = sessionId;
                    setIsReconnecting(true);
                    setReconnectAttempts(0);
                }
            }
        };

        const cleanupPromise = setupSse();

        return () => {
            cleanupPromise.then(cleanupFunc => {
                if (cleanupFunc) {
                    cleanupFunc();
                }
            });
        };
    }, [sessionId, user, isAuthenticated, reconnectTrigger]);
};